import {VNode} from 'snabbdom/vnode';

export interface DataTableAttrs {
  loading?: boolean;
  'multi-select'?: boolean;
  'selection-limit'?: number;
  'empty-message': string;
  'default-sort-field'?: string;
  'default-sort-order'?: number;
  'has-comparison'?: boolean;
  'max-body-height': string;
  'num-sticky-rows'?: number;
  // TODO: remove sticky-columns-count
  'sticky-columns-count'?: number;
  'track-name': string;
  theme: Theme;
}

/**
 * High level theme for table styles.
 *
 * hides the actual config options for easier development until they stabilize.
 */
export enum Theme {
  Funnels = `funnels`,
  Retention = `retention`,
}

/**
 * Low level table style options. Should be considered package private until they stabilize.
 *
 * If you need a change for a new context, add a Theme instead.
 */
export type ThemeSpec = {
  cellBorders: `line` | `gap`;
  cellBorderHover: `none` | `outline`;
  /** whether data cells in the table have are clickable and focusable */
  cellFocusable: boolean;
  cellHeaderBorders: `cell` | `column-group` | `none`;
  cellHeaderShadow: boolean;
  cellBackground: `row-stripe` | `heatmap` | `none`;
  cellShape: `round` | `square`;
  /** whether to apply different justification and padding to first/middle/last elements of a column group */
  justifyCellsWithinGroup: boolean;
  rowGroups: boolean;
};

export interface TableState {
  /** Table's rows to display, could be any array of objects */
  rows: Array<TableRow<TableCell>>;
  /** Number of rows displayed. Like items-menu, mp-table will progressively load more rows when user scrolls */
  numRowsToDisplay: number;
  /** Field that is currently sorted */
  sortField: string;
  /** Sort order, ascending = 1, descending = -1 */
  sortOrder: number;
  /** Whether the user has scrolled the body, so we can set the scroll shadow for titles and make it look sticky */
  hasBodyScrolled: boolean;
  cellToolTip: CellTooltip;
  headers: ColumnGroup[];
}

/** (internal) Represents a stacked column header for the UI to render. */
export interface ColumnGroupHeader extends ColumnGroup {
  /** a map of css key to styles. contains grid formatting. */
  style?: any;
  /** accumulated width of the columns in this header */
  width: number;
  leftPosition: number;
  reverseColumnIndex: number;
  label?: string;

  /** renderer for the title */
  cellRenderer?(columnGroup: ColumnGroupHeader): VNode;
}

/** A grouping of TableColumns */
export interface ColumnGroup<Column = TableColumn> {
  /** Columns in this group */
  columns: Column[];
  /** Title of this group  */
  title?: string;
  /** Whether this group is a sticky column and pinned to the left */
  sticky?: boolean;
}

type SortTransformOptionalProps = {
  sortOrder: number;
};

export type SortTransformFunc<Row = any> = (row: Row, field: string, props?: SortTransformOptionalProps) => number;

export interface TableColumn<Row = any> {
  /** The property inside each item that corresponds to the header */
  field: string;
  /** Title for the column header */
  title?: string;
  /** icon to show in place of a title */
  icon?: string;
  /** Type of field, affects the cell font.
   *
   * text (default) - no special styling.
   * number - mono font on data cells.
   * checkbox - (internal) special styling for the selection checkbox.
   */
  type?: `text` | `number` | `checkbox`;
  /** width, in pixels, of the column */
  width: number;
  /** whether this column should be pinned to the left */
  sticky?: boolean;
  /** background color for data cells. lower in priority than cells[] */
  backgroundColor?: string;

  /** (internal) whether this column is the last of its column group and should have border */
  last?: boolean;
  /** (internal) whether this column is the first of its column group.  */
  first?: boolean;
  /** (internal) true if this is the "last last" column (last column in the last column group) */
  rightmost?: boolean;

  /** whether this columm should have italics title */
  italics?: boolean;
  /** tooltip to give more info on column title, no (i) */
  tooltip?: string;
  /** tooltip alternative to the helptext */
  label?: string;
  /** placement of tooltip. default is bottom. */
  labelPlacement?: string;
  /** Help text shown when user hovers over (i) */
  helpText?: string;
  /** Placement of help text shown when user hovers over (i), e.g top right bottom left */
  helpTextPlacement?: string;
  /** Help text link (i) */
  helpLink?: string;
  /** if the cells can be clicked, hovered or focused */
  isSelectable?: boolean;
  /** If the field can be grouped */
  isGroupable?: boolean;
  /** If the field can be sorted */
  isSortable?: boolean;
  /** Only for text fields, search-filter will be matched against the field */
  isSearchable?: boolean;
  /** Maximum length of the title or column header, anything that exceeds the length will be middle truncated */
  titleMaxLength?: number;
  /** A custom renderer function (usally $mixins[`cell-{field}`]) */
  cellRenderer?: (arg: {item: Row; context: CellRenderContext}) => VNode | string;
  /** A custom function that returns a number for sort comparison */
  sortTransform?: SortTransformFunc<Row>;
  /** A custom function that returns a label for display e.g 1234 -> 1,234 */
  labelTransform?: (row: Row, field: string) => string | boolean | number;
  /** on click callback override for column header */
  onClickCallbackOverride?: (column: TableColumn) => void;
}

export type Columns<T> = Array<TableColumn | ColumnGroup<T>>;

export interface TableCell {
  /** numeric value that this cell contains */
  value: number;
  /** the display string of the value this cell contains */
  displayValue: string;
  /** the background color of this cell */
  backgroundColor?: string;
  /** the text color of this cell */
  textColor?: string;
  /** icon if this cell has one */
  icon?: string;
}

export interface TableRow<T extends Record<string, any> = any> {
  /** Whether the row was newly added by user via a create action */
  isNew?: boolean;
  /** Whether the row should be shown selected. Applies to multi-select tables */
  isSelected?: boolean;
  /** Whether the row is selectable (will disable checkboxes) */
  isSelectDisabled?: boolean;
  /** A string to be displayed via a tooltip when selection is disabled */
  selectDisabledReason?: string;
  /** If this row is selected, override the checkmark color. */
  checkedColor?: string;
  /** Cells data in this row*/
  cells?: T[];
  /** background color for a specific line*/
  backgroundColor?: string;
}

export interface CellRenderContext {
  field: string;
  label: string;
  isComparison: boolean;
  columnIdx: number;
  rowIdx: number;
  totalRows: number;
}

export interface ColumnRenderContext {
  colIndex: number;
  title: string;
}

export interface ModifierKeys {
  alt: boolean;
  ctrl: boolean;
  shift: boolean;
  meta: boolean;
}

export interface CellTooltip {
  left: number;
  top: number;
  text: string;
}

export type CellClickEvent<T = TableRow<TableCell>> = CustomEvent<{
  row: T;
  field: TableColumn['field'];
  context: CellRenderContext;
  modifierKeys: ModifierKeys;
  target: HTMLElement;
}>;

export type RowClickEvent<T = TableRow<TableCell>> = CustomEvent<{
  row: T;
  rowIdx: number;
  modifierKeys: ModifierKeys;
  target: HTMLElement;
}>;

export type SelectionChangeEvent<T extends TableRow<any> = TableRow<TableCell>> = CustomEvent<{
  changed: Array<{isSelected: boolean; row: T}>;
  /** Whether this selection change was caused by the toggle-all button. */
  isToggleAll?: boolean;
  rows: T[];
}>;

export type SortChangeEvent<T = TableRow<TableCell>> = CustomEvent<{
  sortField: TableColumn['field'];
  sortOrder: number;
  /** Whether this sort was initiated by a user action. */
  isHeaderClick: boolean;
  /** current table rows, sorted */
  rows: T[];
}>;
